// =============================================
// authGuard — central handling for an expired/invalid session.
// =============================================
// When an authenticated API call comes back 401, the app must NOT silently fall
// back to stale localStorage (that's what made an expired session look like
// "data loss"). Instead we clear the dead session and bounce the user to /auth
// with a flag so the login screen can explain what happened.
// =============================================

let handled = false;

/** Called when the API reports the session is invalid/expired (HTTP 401). */
export function handleSessionExpired(): void {
  if (handled) return;          // ignore the stampede of 401s from parallel loads
  const onAuthPage = typeof window !== 'undefined' && /\/auth\b/.test(window.location.pathname);
  // Only latch when we're about to reload into /auth (the reload resets it). Already on
  // /auth there is no reload, so latching would swallow the NEXT session's expiry.
  if (!onAuthPage) handled = true;
  try {
    localStorage.removeItem('akt_auth_token');
    localStorage.removeItem('akt_refresh_token');
    localStorage.removeItem('akt_session_user');
    document.cookie = 'akt_session=; path=/; max-age=0';
    sessionStorage.setItem('akt_session_expired', '1');
  } catch { /* storage may be unavailable */ }
  if (typeof window !== 'undefined' && !onAuthPage) {
    window.location.assign('/auth');
  }
}

// =============================================
// Proactive session check
// =============================================
// The server expires an idle session, but the client only found out when some API
// call happened to return 401 — and many screens render from the local cache first,
// so a user returning after the idle window could browse a few (stale) pages before
// being bounced. verifySession() asks the server directly; callers run it on app
// load, on returning to the tab, on route change and on the first interaction after
// a quiet spell, so an expired session goes to /auth immediately.
// =============================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let lastVerified = 0;
let verifying: Promise<boolean> | null = null;

/**
 * Check the session against the server unless it was confirmed within `maxAgeMs`.
 * Resolves false (after bouncing to /auth) only on a definite 401. No token (local /
 * offline mode) or an unreachable server resolves true — offline use keeps working.
 */
export function verifySession(maxAgeMs = 30 * 1000): Promise<boolean> {
  if (handled) return Promise.resolve(false);
  if (Date.now() - lastVerified < maxAgeMs) return Promise.resolve(true);
  if (verifying) return verifying;

  let token: string | null = null;
  try { token = localStorage.getItem('akt_auth_token'); } catch { /* storage unavailable */ }
  if (!token) return Promise.resolve(true);

  verifying = fetch(`${API_BASE}/auth/profile`, { headers: { Authorization: `Bearer ${token}` } })
    .then(async res => {
      if (res.status === 401) { handleSessionExpired(); return false; }
      if (res.ok) {
        lastVerified = Date.now();
        try {
          const body = await res.json();
          const secs = Number(body?.session_remaining_sec);
          if (Number.isFinite(secs) && secs > 0) setSessionDeadline(Date.now() + secs * 1000);
        } catch { /* older server — no countdown */ }
      }
      return true;
    })
    .catch(() => true)            // network error — not an auth failure
    .finally(() => { verifying = null; });
  return verifying;
}

// =============================================
// Client-side countdown
// =============================================
// The browser keeps the moment the server will end the session (from the server's
// own "seconds left", so a wrong PC clock or a sleep/wake doesn't matter). It is
// shared by every open tab through localStorage, so activity in one tab keeps the
// others signed in. SessionTimer counts it down, warns, and signs out at zero —
// without needing the network, which is what made a return from sleep slow.
// =============================================

const DEADLINE_KEY = 'akt_session_deadline';

// The deadline is stored with the tail of the token it belongs to, so a deadline left
// behind by an earlier session can never sign out a fresh login.
function tokenTail(): string {
  try { return (localStorage.getItem('akt_auth_token') || '').slice(-12); } catch { return ''; }
}

export function setSessionDeadline(ms: number): void {
  try { localStorage.setItem(DEADLINE_KEY, `${Math.round(ms)}|${tokenTail()}`); } catch { /* ignore */ }
}

/** Epoch ms the session ends, or null when unknown (offline mode / older server / other session). */
export function getSessionDeadline(): number | null {
  try {
    const [ms, tail] = (localStorage.getItem(DEADLINE_KEY) || '').split('|');
    const v = Number(ms);
    if (!tail || tail !== tokenTail()) return null;
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch { return null; }
}

/** Countdown reached zero: end the session on the server (best effort) and go to /auth. */
export function expireSessionByTimer(): void {
  if (handled) return;
  let token: string | null = null;
  try { token = localStorage.getItem('akt_auth_token'); } catch { /* ignore */ }
  if (token) {
    fetch(`${API_BASE}/auth/logout`, {
      method: 'POST', keepalive: true, headers: { Authorization: `Bearer ${token}` },
    }).catch(() => { /* already expired server-side, or offline */ });
  }
  try { localStorage.removeItem(DEADLINE_KEY); } catch { /* ignore */ }
  handleSessionExpired();
}

/** Did we just bounce here because the session expired? (one-shot read) */
export function consumeSessionExpiredFlag(): boolean {
  try {
    const v = sessionStorage.getItem('akt_session_expired');
    if (v === '1') { sessionStorage.removeItem('akt_session_expired'); return true; }
  } catch { /* ignore */ }
  return false;
}
